import { Button } from "@/components/ui/button";
import { ArrowLeftIcon, DownloadIcon, LoaderIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import Link from "next/link";

interface BackButtonProps {
  previousPathUrl: string | null;
  previousStepUrl: string | undefined;
}

function BackButton({ previousPathUrl, previousStepUrl }: BackButtonProps) {
  const url = previousStepUrl || previousPathUrl;
  if (!url) return <div></div>;

  const buttonText = previousStepUrl ? "Poprzedni krok" : "Poprzednia ścieżka";

  return (
    <Button variant="outline" asChild>
      <Link href={url}>
        <ArrowLeftIcon />
        {buttonText}
      </Link>
    </Button>
  );
}

interface NextButtonProps {
  canAdvance: boolean;
  isLoading: boolean;
  isLastGroup: boolean;
  isLastStep: boolean;
  outroSeen: boolean | undefined;
  hasOtherIncompleteSteps: boolean;
  onNext: () => void;
  onFinish: () => Promise<void>;
}

function NextButton({
  canAdvance,
  isLoading,
  isLastGroup,
  isLastStep,
  outroSeen,
  hasOtherIncompleteSteps,
  onNext,
  onFinish,
}: NextButtonProps) {
  const handleNextClick = async () => {
    if (!isLastGroup) {
      onNext();
    } else {
      await onFinish();
    }
  };

  const resolveNextButtonText = () => {
    if (isLastGroup) {
      // Outro already seen → user is revisiting, go straight to summary
      if (outroSeen && isLastStep) {
        return "Podsumowanie";
      }
      // Last step but other steps still incomplete → navigate to them
      if (isLastStep && hasOtherIncompleteSteps) {
        return "Następny krok";
      }
      // Last step, all complete → finish the path; otherwise next step
      return isLastStep ? "Zakończ ścieżkę" : "Następny krok";
    }
    return "Dalej";
  };

  return (
    <Button
      variant="blue"
      disabled={!canAdvance || isLoading}
      onClick={handleNextClick}
    >
      {isLoading && <LoaderIcon className="animate-spin" size={16} />}
      {resolveNextButtonText()}
    </Button>
  );
}

export function StepNavigationButton({
  canAdvance,
  isLoading,
  isLastGroup,
  onNext,
  onFinish,
  isLastStep,
  outroSeen,
  previousPathUrl,
  previousStepUrl,
  hasOtherIncompleteSteps,
  isChecklist,
  onDownload,
}: {
  canAdvance: boolean;
  isLoading: boolean;
  isLastGroup: boolean;
  onNext: () => void;
  onFinish: () => Promise<void>;
  isLastStep: boolean;
  outroSeen: boolean | undefined;
  previousPathUrl: string | null;
  previousStepUrl: string | undefined;
  hasOtherIncompleteSteps: boolean;
  isChecklist: boolean;
  onDownload: () => void;
}) {
  return (
    <div
      className={cn(
        "fixed right-0 bottom-0 left-0 z-50 -mx-4 border-t bg-white px-8 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.1)] xl:sticky xl:right-auto xl:left-auto xl:z-100 xl:mt-8 xl:px-0 xl:shadow-none print:hidden",
      )}
    >
      <div className="3xl:max-w-[var(--content-width-1024)] @container mx-auto flex max-w-[var(--content-width)] justify-between gap-2">
        <BackButton
          previousPathUrl={previousPathUrl}
          previousStepUrl={previousStepUrl}
        />
        {isChecklist && (
          <Button variant="outline" onClick={onDownload}>
            <DownloadIcon />
            Pobierz
          </Button>
        )}
        <NextButton
          canAdvance={canAdvance}
          isLoading={isLoading}
          isLastGroup={isLastGroup}
          isLastStep={isLastStep}
          outroSeen={outroSeen}
          hasOtherIncompleteSteps={hasOtherIncompleteSteps}
          onNext={onNext}
          onFinish={onFinish}
        />
      </div>
    </div>
  );
}
